import { v4 as uuid } from 'uuid';

import { ProtectorType } from 'core/constants';

import { AuditAction, AuditFormModel, AuditOption, AuditQuestion, AuditSection, CHOICE_TYPES } from './types';

/** The stored form as the API renders it: camelCase, the shape `SubmissionFormSerializer` writes. */
export interface WireQuestion {
  description: string;
  questionType: ProtectorType;
  properties?: Record<string, unknown> | null;
}

export interface WireSection {
  title: string;
  description?: string | null;
  order: string[];
  required?: string[];
  properties: Record<string, WireQuestion>;
  actions?: AuditAction[];
}

export interface WireForm {
  order: string[];
  properties: Record<string, WireSection>;
}

/** Rewritten on save. Everything else in `properties` is carried through untouched. */
const AUTHORED_PROPERTY_KEYS = ['enum', 'lookupType', 'multiple', 'image'];

/** Attached by the server at serve time and not persisted, so echoing it back is noise. */
const SERVED_ONLY_PROPERTY_KEYS = ['repeatingGroup'];

const DROPPED_ON_LOAD = new Set([...AUTHORED_PROPERTY_KEYS, ...SERVED_ONLY_PROPERTY_KEYS]);

export const mintKey = (): string => uuid().slice(0, 8);

const toOption = (item: unknown): AuditOption => {
  const raw = (item ?? {}) as {
    key?: string;
    value?: string;
    score?: number | null;
    colour?: string | null;
  };

  return {
    key: raw.key ?? mintKey(),
    value: raw.value ?? '',
    score: raw.score ?? null,
    colour: raw.colour ?? null,
  };
};

const toQuestion = (key: string, wire: WireQuestion, required: boolean): AuditQuestion => {
  const properties = wire.properties ?? {};
  const unauthoredProperties = Object.fromEntries(
    Object.entries(properties).filter(([name]) => !DROPPED_ON_LOAD.has(name))
  );
  const image = properties.image as { uuid: string; file?: string } | null | undefined;

  return {
    key,
    description: wire.description ?? '',
    questionType: wire.questionType,
    required,
    options: ((properties.enum as unknown[] | null | undefined) ?? []).map(toOption),
    lookupType: (properties.lookupType as string | null | undefined) ?? null,
    multiple: Boolean(properties.multiple),
    image: image ?? null,
    unauthoredProperties,
  };
};

export const formToModel = (form: WireForm): AuditFormModel => ({
  sections: form.order.map((sectionKey) => {
    const section = form.properties[sectionKey];
    const required = new Set(section.required ?? []);

    return {
      key: sectionKey,
      title: section.title ?? '',
      description: section.description ?? '',
      questions: section.order.map((questionKey) =>
        toQuestion(questionKey, section.properties[questionKey], required.has(questionKey))
      ),
      actions: section.actions ?? [],
    };
  }),
});

const questionToWire = (question: AuditQuestion): WireQuestion => {
  const isChoice = CHOICE_TYPES.includes(question.questionType);
  const properties: Record<string, unknown> = { ...question.unauthoredProperties };

  // The server nulls `enum` for every other type anyway.
  if (isChoice) {
    properties.enum = question.options.map(({ key, value, score, colour }) => ({ key, value, score, colour }));
  }

  if (question.questionType === ProtectorType.Lookup) {
    if (question.lookupType) properties.lookupType = question.lookupType;
    properties.multiple = question.multiple;
  }

  // `file` is read-only server-side.
  if (question.image) properties.image = { uuid: question.image.uuid };

  return {
    description: question.description,
    questionType: question.questionType,
    properties,
  };
};

export const modelToForm = (model: AuditFormModel, { conditionalLogic = true } = {}): WireForm => ({
  order: model.sections.map((section) => section.key),
  properties: Object.fromEntries(
    model.sections.map((section) => [
      section.key,
      {
        title: section.title,
        description: section.description,
        order: section.questions.map((question) => question.key),
        required: section.questions.filter((question) => question.required).map((question) => question.key),
        properties: Object.fromEntries(section.questions.map((question) => [question.key, questionToWire(question)])),
        actions: conditionalLogic ? section.actions : [],
      },
    ])
  ),
});
